// URL ↔ section mapping for the Scenes menu.
//
// Every URL the book ever served keeps working (printed QR cards, shares),
// plus the short aliases from the design handoff. The address bar is kept
// in sync with the section under the header via replaceState only — the
// browser's Back never has to walk through every section.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { ROUTES, CANON, TITLES, resolve } from './routes.js';

export { ROUTES, CANON, TITLES, resolve };


/**
 * @param {{ engine: { goSection(id: string, full?: boolean, behavior?: string): void, onSection(cb: (id: string) => void): () => void }, skipIntro: () => void }} opts
 */
export default function useRoute({ engine, skipIntro }) {
  const current = useRef('s-top');
  const timer = useRef(0);

  // Initial deep link — before first paint, so the section is the first
  // thing painted (a real LCP) rather than the entry screen followed by a
  // jump. List pages land with their top at the header's bottom edge;
  // pinned scenes/openers land at 0 (goSection knows which is which).
  // Once the web fonts are in, the landing is re-aligned exactly once,
  // and only if nobody has scrolled in the meantime.
  useLayoutEffect(() => {
    try { history.scrollRestoration = 'manual'; } catch (_) { /* ignore */ }
    const id = resolve(location.pathname);
    current.current = id;
    document.title = TITLES[id] || TITLES['s-top'];
    if (id !== 's-top') {
      skipIntro();
      engine.goSection(id, false, 'auto');
    }
    engine.settle();
    if (id === 's-top') return undefined;
    let cancelled = false;
    const realign = () => {
      if (cancelled) return;
      const app = engine.app();
      if (app && Math.abs(app.scrollTop - engine.lastGoY) < 2) engine.goSection(id, false, 'auto');
    };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(realign).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll-spy → address bar (replaceState, debounced) + title.
  useEffect(() => {
    const write = (id) => {
      const path = CANON[id];
      if (path && location.pathname !== path) {
        try { history.replaceState(history.state, '', path); } catch (_) { /* ignore */ }
      }
      document.title = TITLES[id] || TITLES['s-top'];
    };
    // Flush any pending URL write NOW — the detail overlay calls this before
    // pushing its own history entry so Back never lands on a stale section.
    engine.syncUrl = () => { clearTimeout(timer.current); write(current.current); };
    const off = engine.onSection((id) => {
      if (id === current.current) return;
      current.current = id;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => write(id), 150);
    });
    return () => { off(); clearTimeout(timer.current); engine.syncUrl = null; };
  }, [engine]);

  // Back / forward.
  useEffect(() => {
    const onPop = () => {
      if (engine.detailOpen) return; // the detail overlay owns this Back
      const id = resolve(location.pathname);
      if (id === current.current) return; // same section — never jump
      current.current = id;
      engine.goSection(id, false, 'auto');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [engine]);
}
